import { useEffect, useMemo } from 'react'
import { FilterList } from '@mui/icons-material'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import { ByTimeMetricReport } from '@/types/workflow-metric-reports'
import useOverlay from '@/hooks/useOverlay'
import useSessionStorage from '@/hooks/useSessionStorage'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import useGetWorkflowStateMenuItems from '@/components/workflow-state-dropdown/useGetWorkflowStateMenuItems'

type UseWorkflowSelectedStateIdsOptions = {
  report?: ByTimeMetricReport
  key: string
}

type MetricsStatesFilterProps = {
  allStateIds?: string[]
  selectedStateIds?: string[]
  setSelectedStateIds: (
    v:
      | string[]
      | ((val: string[] | undefined) => string[] | undefined)
      | undefined,
  ) => void
}

export function useWorkflowSelectedStateIds({
  key,
  report,
}: UseWorkflowSelectedStateIdsOptions) {
  const stateIds = useMemo(() => {
    return (
      report?.reports[0].reports.map((r) => r.workflow_state_id) || undefined
    )
  }, [report])

  const [selectedStateIds, setSelectedStateIds] = useSessionStorage(
    `card-metrics-${key}-states-ids`,
    stateIds,
  )

  useEffect(() => {
    if (stateIds && !selectedStateIds) {
      setSelectedStateIds(stateIds)
    }
  }, [stateIds, selectedStateIds, setSelectedStateIds])

  const filteredStateIds = useMemo(
    () =>
      selectedStateIds?.length === stateIds?.length
        ? undefined
        : selectedStateIds,
    [selectedStateIds, stateIds],
  )

  return {
    allStateIds: stateIds,
    filteredStateIds,
    selectedStateIds,
    setSelectedStateIds,
  }
}

export default function MetricsStatesFilter({
  allStateIds,
  selectedStateIds,
  setSelectedStateIds,
}: MetricsStatesFilterProps) {
  const statesFilterOverlay = useOverlay()
  const { selectedWorkflow } = useSelectedWorkflowContext()

  const workflowStates = useMemo(
    () =>
      selectedWorkflow.workflow_states?.filter(({ id, code }) =>
        allStateIds ? allStateIds.includes(id) : code !== 'processing',
      ) || [],
    [selectedWorkflow.workflow_states, allStateIds],
  )

  const workflowStateMenuItems = useGetWorkflowStateMenuItems({
    workflowStates,
    multiSelect: true,
    typographyVariant: 'body1',
    selectedWorkflowStateIds: selectedStateIds,
    onChange: (state, isSelected) => {
      setSelectedStateIds((prev: string[] | undefined) => {
        const updated = prev ? prev : []
        return isSelected
          ? [...updated, state.id]
          : updated.filter((id: string) => id !== state.id)
      })
    },
  })

  return (
    <>
      <Tooltip title="Filter" enterDelay={1000}>
        <div>
          <IconButton
            onClick={statesFilterOverlay.open}
            disabled={!allStateIds}
            size="small"
          >
            <FilterList fontSize="small" />
          </IconButton>
        </div>
      </Tooltip>
      <Dialog title="States" maxWidth="xs" {...statesFilterOverlay}>
        <DialogContent>
          <Button
            disabled={!allStateIds}
            variant="text"
            onClick={() => {
              if (allStateIds?.length === selectedStateIds?.length) {
                setSelectedStateIds([])
              } else {
                setSelectedStateIds(allStateIds)
              }
            }}
            sx={{
              mb: 1,
              ml: -0.5,
            }}
          >
            {allStateIds?.length === selectedStateIds?.length
              ? 'Deselect'
              : 'Select'}{' '}
            All
          </Button>
          <Box sx={{ mx: '3px' }}>{workflowStateMenuItems}</Box>
        </DialogContent>
        <DialogFooter>
          <Button onClick={statesFilterOverlay.close} variant="text">
            Close
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}
